// src/shared/composables/useFileUploader.ts
//
// Orchestrates: validate size → encrypt with WebCrypto → POST ciphertext.
// Returns a share URL with the passphrase in the URL fragment (`#`).

import { ref } from 'vue'
import { useApi } from './useApi'
import {
  encryptFile,
  encryptFileChunked,
  generatePassphrase,
  CHUNK_SIZE,
} from './useE2EEncryption'
import {
  uploadEncryptedFile,
  initChunkedUpload,
  putChunk,
  finalizeChunkedUpload,
  type UploadFileResponse,
} from '@/api/files'

export interface UploaderOptions {
  /** Hard cap (bytes) from the tenant's plan (max_file_size_bytes). */
  maxBytes?: number
  /** Seconds — TTL of the SecretFile in Redis. Default 24h. */
  ttlSeconds?: number
  /** When true, generate passphrase automatically. When false, caller supplies. */
  autoPassphrase?: boolean
}

export interface UploadResult {
  extid: string
  shareUrl: string
  passphrase: string
  expiresAt: number | null
}

export function useFileUploader() {
  const api = useApi()
  const uploading = ref(false)
  const error = ref<string | null>(null)

  async function upload(
    file: File,
    opts: UploaderOptions = {},
    customPassphrase?: string,
  ): Promise<UploadResult> {
    error.value = null
    uploading.value = true
    try {
      if (opts.maxBytes && file.size > opts.maxBytes) {
        throw new Error(`File too large (${file.size} > ${opts.maxBytes})`)
      }

      const passphrase = customPassphrase ?? generatePassphrase(32)

      // --- Ruta chunked: ficheros que superan CHUNK_SIZE (4 MiB) ---
      if (file.size > CHUNK_SIZE) {
        const enc = await encryptFileChunked(file, passphrase)
        const init = await initChunkedUpload(api, {
          salt_b64: enc.saltB64,
          base_nonce_b64: enc.baseNonceB64,
          total_chunks: enc.totalChunks,
          chunk_size: enc.chunkSize,
          total_size: enc.totalSize,
          ttl: opts.ttlSeconds ?? 86_400,
        })
        // Subida con concurrencia máx. 2, un reintento ante fallo transitorio
        const queue = enc.chunksB64.map((b64, i) => ({ b64, i }))
        const MAX_CONCURRENCY = 2
        let cursor = 0
        async function worker() {
          while (cursor < queue.length) {
            const { b64, i } = queue[cursor++]
            let attempts = 0
            // eslint-disable-next-line no-constant-condition
            while (true) {
              try {
                await putChunk(api, init.extid, i, b64, init.upload_token)
                break
              } catch (e) {
                if (++attempts >= 2) throw e
              }
            }
          }
        }
        await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENCY, queue.length) }, worker))
        const fin = await finalizeChunkedUpload(api, init.extid, init.upload_token)
        const base = window.location.origin
        return {
          extid: fin.extid,
          shareUrl: `${base}/file/${encodeURIComponent(fin.extid)}#${encodeURIComponent(passphrase)}`,
          passphrase,
          expiresAt: fin.expires_at,
        }
      }

      // --- Ruta mono (fichero pequeño ≤ CHUNK_SIZE) ---
      const payload = await encryptFile(file, passphrase)

      const response: UploadFileResponse = await uploadEncryptedFile(api, {
        iv_b64: payload.ivB64,
        salt_b64: payload.saltB64,
        content_base64: payload.ciphertextB64,
        ttl: opts.ttlSeconds ?? 86_400,
        // Intentionally NOT sending mime_type_hint — the real MIME stays
        // inside the encrypted metadata blob.
      })

      const base = window.location.origin
      const shareUrl = `${base}/file/${encodeURIComponent(response.record.extid)}#${encodeURIComponent(passphrase)}`

      return {
        extid: response.record.extid,
        shareUrl,
        passphrase,
        expiresAt: response.record.expires_at,
      }
    } catch (ex) {
      const msg = ex instanceof Error ? ex.message : String(ex)
      error.value = msg
      throw ex
    } finally {
      uploading.value = false
    }
  }

  return { upload, uploading, error }
}
